/**
 * Command Palette — a VS-Code-style fuzzy search overlay for all commands.
 */

import { useT } from "@magic-md/i18n/react";
import { Command as CommandIcon, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    type Command,
    executeCommand,
    formatShortcut,
    getAllCommands,
} from "../lib/commands";
import { cn } from "../lib/utils";

interface CommandPaletteProps {
    open: boolean;
    onClose: () => void;
}

/** Simple fuzzy match: check if all chars of query appear in order in target */
function fuzzyMatch(query: string, target: string): boolean {
    const q = query.toLowerCase();
    const t = target.toLowerCase();
    let qi = 0;
    for (let ti = 0; ti < t.length && qi < q.length; ti++) {
        if (t[ti] === q[qi]) qi++;
    }
    return qi === q.length;
}

/** Score a match — lower = better */
function fuzzyScore(query: string, target: string): number {
    const q = query.toLowerCase();
    const t = target.toLowerCase();
    let qi = 0;
    let score = 0;
    let lastIdx = -1;
    for (let ti = 0; ti < t.length && qi < q.length; ti++) {
        if (t[ti] === q[qi]) {
            // Bonus for consecutive chars
            score += ti === lastIdx + 1 ? 0 : ti - (lastIdx + 1);
            lastIdx = ti;
            qi++;
        }
    }
    return score;
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
    const t = useT("editor");
    const [query, setQuery] = useState("");
    const [selectedIdx, setSelectedIdx] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    // Re-fetch commands every time the palette opens so we pick up
    // commands registered after the initial render.
    const commands = useMemo(() => getAllCommands(), [open]);

    const filtered = useMemo(() => {
        if (!query) return commands;
        return commands
            .filter(
                (c) =>
                    fuzzyMatch(query, c.label) || fuzzyMatch(query, c.category ?? ""),
            )
            .sort((a, b) => fuzzyScore(query, a.label) - fuzzyScore(query, b.label));
    }, [query, commands]);

    // Reset when opened
    useEffect(() => {
        if (open) {
            setQuery("");
            setSelectedIdx(0);
            requestAnimationFrame(() => inputRef.current?.focus());
        }
    }, [open]);

    // Scroll selected item into view
    useEffect(() => {
        const el = listRef.current?.children[selectedIdx] as
            | HTMLElement
            | undefined;
        el?.scrollIntoView({ block: "nearest" });
    }, [selectedIdx]);

    const run = useCallback(
        (cmd: Command) => {
            onClose();
            // Small delay so the palette closes before the command runs
            requestAnimationFrame(() => executeCommand(cmd.id));
        },
        [onClose],
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            switch (e.key) {
                case "ArrowDown":
                    e.preventDefault();
                    setSelectedIdx((i) => Math.min(i + 1, filtered.length - 1));
                    break;
                case "ArrowUp":
                    e.preventDefault();
                    setSelectedIdx((i) => Math.max(i - 1, 0));
                    break;
                case "Enter":
                    e.preventDefault();
                    if (filtered[selectedIdx]) {
                        run(filtered[selectedIdx]);
                    }
                    break;
                case "Escape":
                    e.preventDefault();
                    onClose();
                    break;
            }
        },
        [filtered, selectedIdx, run, onClose],
    );

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]"
            role="presentation"
            onMouseDown={onClose}
        >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/30" />

            {/* Palette */}
            <div
                className="relative w-full max-w-lg rounded-xl border border-border bg-background shadow-2xl overflow-hidden"
                onMouseDown={(e) => e.stopPropagation()}
                role="dialog"
                aria-label={t("app.commandPalette")}
            >
                {/* Search input */}
                <div className="flex items-center gap-2 px-3 border-b border-border">
                    <Search size={16} className="text-muted-foreground shrink-0" />
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setSelectedIdx(0);
                        }}
                        onKeyDown={handleKeyDown}
                        placeholder={t("command.placeholder")}
                        className="flex-1 py-3 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
                        autoComplete="off"
                        spellCheck={false}
                    />
                </div>

                {/* Command list */}
                <div ref={listRef} className="max-h-64 overflow-y-auto py-1">
                    {filtered.length === 0 && (
                        <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                            {t("command.empty")}
                        </div>
                    )}
                    {filtered.map((cmd, idx) => (
                        <button
                            type="button"
                            key={cmd.id}
                            className={cn(
                                "w-full flex items-center gap-3 px-3 py-2 text-sm text-left transition-colors",
                                idx === selectedIdx
                                    ? "bg-primary/10 text-foreground"
                                    : "text-foreground/80 hover:bg-muted",
                            )}
                            onClick={() => run(cmd)}
                            onMouseEnter={() => setSelectedIdx(idx)}
                        >
                            <CommandIcon
                                size={14}
                                className="text-muted-foreground shrink-0"
                            />
                            <span className="flex-1 truncate">
                                {cmd.category && (
                                    <span className="text-muted-foreground mr-1.5">
                                        {cmd.category}:
                                    </span>
                                )}
                                {cmd.label}
                            </span>
                            {cmd.shortcut && (
                                <kbd className="ml-auto text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded font-mono">
                                    {formatShortcut(cmd.shortcut)}
                                </kbd>
                            )}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}
